import React, { useContext, useEffect, useMemo, useState } from 'react'
import {
    FiArrowUpRight, FiCheckCircle, FiChevronRight,
    FiClock,
    FiFilter,
    FiLayers,
    FiMapPin,
    FiMessageSquare, FiPhone, FiRefreshCw, FiSearch, FiSend, FiSliders, FiTag, FiUser, FiUsers, FiX
} from 'react-icons/fi'
import { useParams } from 'react-router'
import { Link } from 'react-router-dom'
import { getAge, humanReadableTimePassed } from '../../Helper/helpers'
import { BASE_URL_API } from '../../References/urls'
import AuthContext from '../../store/auth-context'
import styles from '../EscalatedCase/EscalatedCaseJobs.module.css'
import Select from 'react-select'
import { executivesData as storedExecutives, useExecutives, getExecutiveFullName } from '../../store/ExecutiveContext'

const EscalatedCaseJobs = () => {
    const authCtx = useContext(AuthContext)
    const executivesOptions = storedExecutives?.map(e => ({
                    value: e?.userID,
                    label: getExecutiveFullName(e?.userID),
                })) ?? []
                
    const [selectedExecutive, setSelectedExecutive] = useState(null)

    const params = useParams()
    const activeCaseIdentifer = params.caseIdentifier
    const STANDARD_STATUS = ["open", "assigned", "resolved", "closed", "in_progress", "on_hold"]
    const [selectedStatus, setSelectedStatus] = useState(['open'])
    const statusOptions = useMemo(() => {
        return STANDARD_STATUS.map((status) => ({
            value: status,
            label: status.replace('_', ' ').toUpperCase()
        }))
    }, [])

    const [cases, setCases] = useState([])
    const [fetchingCaseData, setFetchingCaseData] = useState(false)
    const [activeCaseData, setActiveCaseData] = useState(null)
    const [caseStatusMap, setCaseStatusMap] = useState({})
    const [localNotesMap, setLocalNotesMap] = useState({})

    const [searchText, setSearchText] = useState('')
    const [statusFilter, setStatusFilter] = useState('ALL')
    const [domainFilter, setDomainFilter] = useState('ALL')
    const [sortBy, setSortBy] = useState('LATEST')
    const [groupBy, setGroupBy] = useState('NONE')

    const [selectedCaseIdentifiers, setSelectedCaseIdentifiers] = useState([])
    const [bulkStatus, setBulkStatus] = useState('IN_PROGRESS')
    const [bulkNote, setBulkNote] = useState('')

    const [response, setResponse] = useState('')

    const mapStatusFromCase = (item) => {
        if (item?.isResolved === '1') {
            return 'RESOLVED'
        }
        return 'OPEN'
    }

    const getCaseStatus = (item) => {
        return caseStatusMap[item?.caseIdentifier] || mapStatusFromCase(item)
    }

    const getPriorityFromSubject = (subject = '') => {
        const normalizedSubject = String(subject).toUpperCase()
        if (normalizedSubject.includes('URGENT') || normalizedSubject.includes('CRITICAL')) {
            return 'HIGH'
        }
        if (normalizedSubject.includes('NEED') || normalizedSubject.includes('JOB')) {
            return 'MEDIUM'
        }
        return 'LOW'
    }

    const getPriorityClass = (priority = 'LOW') => {
        if (priority === 'HIGH') {
            return styles.priorityHigh
        }
        if (priority === 'MEDIUM') {
            return styles.priorityMedium
        }
        return styles.priorityLow
    }

    const getStatusClass = (status = 'OPEN') => {
        if (status === 'RESOLVED') {
            return styles.statusResolved
        }
        if (status === 'IN_PROGRESS') {
            return styles.statusInProgress
        }
        return styles.statusOpen
    }

    const toTimestamp = (dateTime) => {
        const value = Date.parse(dateTime)
        if (Number.isNaN(value)) {
            return 0
        }
        return value
    }

    const formatDate = (dateTime) => {
        if (!dateTime || dateTime === '0000-00-00 00:00:00') {
            return '--'
        }
        return String(dateTime).substring(0, 10)
    }

    const getEscalatedCaseForAdmin = async () => {
        const gecfaResponse = await fetch(BASE_URL_API + "/getEscalatedCaseForAdmin",
            {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx.token,
                    executiveIdentifier: authCtx.userIdentifier,
                    selectedStatus: selectedStatus.length ? selectedStatus : null,
                    selectedExecutive: selectedExecutive.length ? selectedExecutive : null
                })
            });

        if (!gecfaResponse.ok) {
            console.log("Something went wrong : Server Error")
        } else {
            const gecfaRespo = await gecfaResponse.json()
            if (gecfaRespo.status === "success") {
                setCases(gecfaRespo.response)
            } else {
                if (gecfaRespo.message === "token Expired, please login again!") {
                    authCtx.logout()
                } else {
                    setCases([])
                }
            }
            console.log(gecfaRespo)
        }
    }

    useEffect(() => {
        getEscalatedCaseForAdmin()
    }, [selectedStatus, selectedExecutive])

    const getEscalatedCaseInfo = async () => {
        setFetchingCaseData(true)
        const getEscalatedCaseInfoResponse = await fetch(BASE_URL_API + "/getEscalatedCaseInfo",
            {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx.token,
                    executiveIdentifier: authCtx.userIdentifier,
                    caseIdentifier: activeCaseIdentifer
                })
            });

        if (!getEscalatedCaseInfoResponse.ok) {
            console.log("Something went wrong : Server Error")
        } else {
            const getEscalatedCaseInfoRespo = await getEscalatedCaseInfoResponse.json();
            if (getEscalatedCaseInfoRespo.status === "success") {
                setActiveCaseData(getEscalatedCaseInfoRespo.response)
            } else {
                if (getEscalatedCaseInfoRespo.message === "token Expired, please login again!") {
                    authCtx.logout()
                } else {
                    setActiveCaseData(null)
                }
            }
            console.log("escalated case info ", getEscalatedCaseInfoRespo)
        }
        setFetchingCaseData(false)
    }

    useEffect(() => {
        if (activeCaseIdentifer !== null) {
            getEscalatedCaseInfo()
        }
    }, [activeCaseIdentifer])

    const addEscalatedCaseResponse = async ({ caseIdentifier, remarks, msgType = 'INCOMING', subject = 'URGENTLY_NEED_JOB', isUpdatingStatus, updatingStatus }) => {
        if (!caseIdentifier || caseIdentifier === 'null' || !remarks?.trim()) {
            return false
        }

        const aecrResponse = await fetch(BASE_URL_API + "/addEscalatedCaseResponse",
            {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx.token,
                    executiveIdentifier: authCtx.userIdentifier,
                    caseIdentifier,
                    remarks,
                    msgType,
                    subject,
                    isUpdatingStatus: isUpdatingStatus || false,
                    updatingStatus: updatingStatus || null
                })
            });

        if (!aecrResponse.ok) {
            console.log("Something went wrong : Server Error")
            return false
        } else {
            const aecrRespo = await aecrResponse.json();
            if (aecrRespo.status === "success") {
                return true
            } else {
                if (aecrRespo.message === "token Expired, please login again!") {
                    authCtx.logout()
                } else {

                }
            }
            console.log(aecrRespo);
        }

        return false
    }

    const sendResponseHandler = async () => {
        if (!response.trim() || !activeCaseData?.length || activeCaseIdentifer === 'null') {
            return
        }

        const temp = {
            ID: 'x',
            caseIdentifier: activeCaseIdentifer,
            remarks: response,
            addedOn: new Date().toISOString().replace('T', ' ').substring(0, 19),
            msgType: 'INCOMING',
            actionBy: authCtx.userIdentifier
        }

        setActiveCaseData((prev) => [...(prev || []), temp])

        const isSuccess = await addEscalatedCaseResponse({
            caseIdentifier: activeCaseIdentifer,
            remarks: response,
            msgType: 'INCOMING',
            subject: activeCaseData?.[0]?.subject || 'URGENTLY_NEED_JOB'
        })

        if (isSuccess) {
            setResponse('')
            getEscalatedCaseInfo()
        }
    }

    const activeCaseItem = useMemo(() => {
        return cases.find((item) => item.caseIdentifier === activeCaseIdentifer) || null
    }, [cases, activeCaseIdentifer])

    const uniqueDomains = useMemo(() => {
        const domains = new Set()
        cases.forEach((item) => {
            if (item.primaryWorkDomain) {
                domains.add(item.primaryWorkDomain)
            }
            if (item.secondaryWorkDomain) {
                domains.add(item.secondaryWorkDomain)
            }
            if (item.tertiaryWorkDomain) {
                domains.add(item.tertiaryWorkDomain)
            }
        })
        return ['ALL', ...Array.from(domains)]
    }, [cases])

    const filteredAndSortedCases = useMemo(() => {
        const normalizedSearch = searchText.trim().toLowerCase()

        const filteredCases = cases.filter((item) => {
            const status = getCaseStatus(item)

            if (statusFilter !== 'ALL' && status !== statusFilter) {
                return false
            }

            if (domainFilter !== 'ALL') {
                const domains = [item.primaryWorkDomain, item.secondaryWorkDomain, item.tertiaryWorkDomain]
                    .filter(Boolean)
                    .map((domain) => String(domain).toLowerCase())

                if (!domains.includes(String(domainFilter).toLowerCase())) {
                    return false
                }
            }

            if (!normalizedSearch) {
                return true
            }

            const searchableContent = [
                item.caseIdentifier,
                item.stylist_name,
                item.contact_number,
                item.subject,
                item.currentLocation,
                item.primaryWorkDomain,
                item.secondaryWorkDomain,
                item.tertiaryWorkDomain
            ].join(' ').toLowerCase()

            return searchableContent.includes(normalizedSearch)
        })

        const sortedCases = [...filteredCases].sort((a, b) => {
            if (sortBy === 'LATEST') {
                return toTimestamp(b.addedOn) - toTimestamp(a.addedOn)
            }
            if (sortBy === 'OLDEST') {
                return toTimestamp(a.addedOn) - toTimestamp(b.addedOn)
            }
            if (sortBy === 'SALARY_HIGH') {
                return Number(b.minimumSalaryExpectation || 0) - Number(a.minimumSalaryExpectation || 0)
            }
            if (sortBy === 'SALARY_LOW') {
                return Number(a.minimumSalaryExpectation || 0) - Number(b.minimumSalaryExpectation || 0)
            }
            if (sortBy === 'NAME') {
                return String(a.stylist_name || '').localeCompare(String(b.stylist_name || ''))
            }
            return 0
        })

        return sortedCases
    }, [cases, searchText, statusFilter, domainFilter, sortBy, caseStatusMap])

    const groupedCases = useMemo(() => {
        const grouped = {}
        filteredAndSortedCases.forEach((item) => {
            let key = 'All Tickets'

            if (groupBy === 'STATUS') {
                key = getCaseStatus(item)
            }

            if (groupBy === 'DOMAIN') {
                key = item.primaryWorkDomain || 'No Domain'
            }

            if (groupBy === 'GENDER') {
                key = item.stylist_gender || 'Unknown'
            }

            if (!grouped[key]) {
                grouped[key] = []
            }

            grouped[key].push(item)
        })

        return grouped
    }, [filteredAndSortedCases, groupBy, caseStatusMap])

    const selectedCount = selectedCaseIdentifiers.length

    const totalCount = cases.length
    const openCount = cases.filter((item) => getCaseStatus(item) === 'OPEN').length
    const inProgressCount = cases.filter((item) => getCaseStatus(item) === 'IN_PROGRESS').length
    const resolvedCount = cases.filter((item) => getCaseStatus(item) === 'RESOLVED').length

    const toggleCaseSelection = (caseIdentifier) => {
        setSelectedCaseIdentifiers((prev) => {
            if (prev.includes(caseIdentifier)) {
                return prev.filter((id) => id !== caseIdentifier)
            }
            return [...prev, caseIdentifier]
        })
    }

    const clearSelection = () => {
        setSelectedCaseIdentifiers([])
    }

    const applyBulkUpdate = async () => {
        if (!selectedCaseIdentifiers.length) {
            return
        }

        const selectionSet = new Set(selectedCaseIdentifiers)

        setCaseStatusMap((prev) => {
            const next = { ...prev }
            selectedCaseIdentifiers.forEach((identifier) => {
                next[identifier] = bulkStatus
            })
            return next
        })

        if (bulkNote.trim()) {
            const noteRecord = {
                id: Date.now(),
                text: bulkNote,
                addedOn: new Date().toISOString(),
                by: authCtx.userIdentifier
            }
            setLocalNotesMap((prev) => {
                const next = { ...prev }
                selectedCaseIdentifiers.forEach((identifier) => {
                    next[identifier] = [...(next[identifier] || []), noteRecord]
                })
                return next
            })
        }

        const bulkRemark = `Status updated to ${bulkStatus}${bulkNote.trim() ? ` | Note: ${bulkNote.trim()}` : ''}`
        await Promise.all(selectedCaseIdentifiers.map((identifier) => addEscalatedCaseResponse({
            caseIdentifier: identifier,
            remarks: bulkRemark,
            subject: 'CASE_STATUS_UPDATE',
            msgType: 'INCOMING'
        })))

        if (selectionSet.has(activeCaseIdentifer) && activeCaseIdentifer !== 'null') {
            getEscalatedCaseInfo()
        }

        setBulkNote('')
    }

    const updateSingleCaseStatus = async (nextStatus) => {
        if (!activeCaseItem) {
            return
        }

        setCaseStatusMap((prev) => ({
            ...prev,
            [activeCaseItem.caseIdentifier]: nextStatus
        }))

        await addEscalatedCaseResponse({
            caseIdentifier: activeCaseItem.caseIdentifier,
            remarks: `Status updated to ${nextStatus}`,
            subject: 'CASE_STATUS_UPDATE',
            msgType: 'INCOMING',
            isUpdatingStatus: true,
            updatingStatus: nextStatus
        })

        if (activeCaseIdentifer !== 'null') {
            getEscalatedCaseInfo()
        }
    }

    const addLocalNoteToActiveCase = () => {
        if (!bulkNote.trim() || !activeCaseItem) {
            return
        }

        const noteRecord = {
            id: Date.now(),
            text: bulkNote,
            addedOn: new Date().toISOString(),
            by: authCtx.userIdentifier
        }

        setLocalNotesMap((prev) => ({
            ...prev,
            [activeCaseItem.caseIdentifier]: [...(prev[activeCaseItem.caseIdentifier] || []), noteRecord]
        }))

        setBulkNote('')
    }

    const activeCaseStatus = activeCaseItem ? getCaseStatus(activeCaseItem) : 'OPEN'
    const activeCasePriority = activeCaseItem ? getPriorityFromSubject(activeCaseItem.subject) : 'LOW'
    const activeLocalNotes = activeCaseItem ? (localNotesMap[activeCaseItem.caseIdentifier] || []) : []

    console.log("selected Executive ", selectedExecutive)

    return (
        <div className={styles.mainWrapper}>
            <div className={styles.mainHeader}>
                <div className={styles.breadcrumb}>
                    <Link to="/" className={styles.breadcrumbLink}>Home</Link>
                    <FiChevronRight size={12} />
                    <span>Escalated Cases</span>
                </div>
                <div className={styles.kpiRow}>
                    <div className={styles.kpiChip}><FiLayers size={12} />{totalCount}</div>
                    <div className={`${styles.kpiChip} ${styles.kpiOpen}`}><FiClock size={12} />{openCount}</div>
                    <div className={`${styles.kpiChip} ${styles.kpiProgress}`}><FiRefreshCw size={12} />{inProgressCount}</div>
                    <div className={`${styles.kpiChip} ${styles.kpiResolved}`}><FiCheckCircle size={12} />{resolvedCount}</div>
                </div>
                <div className={styles.kpiRow}>
                    <Select
                        options={statusOptions}
                        placeholder="Select Status"
                        isMulti={true}
                        isSearchable
                        closeMenuOnSelect={false}
                        className={styles.statusSelect}
                        value={statusOptions.filter((option) => selectedStatus.includes(option.value))}
                        onChange={(options) => setSelectedStatus(options ? options.map((option) => option.value) : [])}
                    />

                    <Select
                        options={executivesOptions}
                        placeholder="Select Escalated By"
                        className={styles.selectBox}
                        isMulti
                        isSearchable
                        className={styles.statusSelect}
                        value={executivesOptions.filter((option) => selectedExecutive && selectedExecutive.includes(option.value))}
                        onChange={(options) => setSelectedExecutive(options ? options.map((option) => option.value) : [])}

                    />
                </div>


            </div>
            <div className={styles.mainBody}>
                <div className={styles.leftCol}>
                    <div className={styles.caseListFilter}>
                        <div className={styles.searchBox}>
                            <FiSearch size={12} className={styles.controlIcon} />
                            <input
                                value={searchText}
                                onChange={(event) => setSearchText(event.target.value)}
                                placeholder="Search by case, stylist, subject, location"
                            />
                        </div>
                        <div className={styles.filterRow}>


                            <div className={styles.compactSelect}>
                                <FiTag size={12} className={styles.controlIcon} />
                                <select value={domainFilter} onChange={(event) => setDomainFilter(event.target.value)}>
                                    {uniqueDomains.map((domain) => (
                                        <option key={domain} value={domain}>{domain === 'ALL' ? 'All Domains' : domain}</option>
                                    ))}
                                </select>
                            </div>
                            {/* </div>

                        <div className={styles.filterRow}> */}
                            <div className={styles.compactSelect}>
                                <FiSliders size={12} className={styles.controlIcon} />
                                <select value={sortBy} onChange={(event) => setSortBy(event.target.value)}>
                                    <option value="LATEST">Latest</option>
                                    <option value="OLDEST">Oldest</option>
                                    <option value="SALARY_HIGH">Salary High</option>
                                    <option value="SALARY_LOW">Salary Low</option>
                                    <option value="NAME">Name A-Z</option>
                                </select>
                            </div>

                            {/* <div className={styles.compactSelect}>
                                <FiLayers size={12} className={styles.controlIcon} />
                                <select value={groupBy} onChange={(event) => setGroupBy(event.target.value)}>
                                    <option value="NONE">No Grouping</option>
                                    <option value="STATUS">Group by Status</option>
                                    <option value="DOMAIN">Group by Domain</option>
                                    <option value="GENDER">Group by Gender</option>
                                </select>
                            </div> */}
                        </div>

                        <div className={styles.bulkBar}>
                            <div className={styles.bulkLeft}>
                                <FiUsers size={12} />
                                <span>{selectedCount} selected | { Object.values(groupedCases).reduce((acc, group) => acc + group.length, 0)} total</span>
                                {selectedCount > 0 && (
                                    <button className={styles.linkBtn} onClick={clearSelection}>
                                        <FiX size={12} /> Clear
                                    </button>
                                )}
                            </div>

                            {/* <div className={styles.bulkControls}>
                                <select value={bulkStatus} onChange={(event) => setBulkStatus(event.target.value)}>
                                    <option value="OPEN">Open</option>
                                    <option value="IN_PROGRESS">In Progress</option>
                                    <option value="RESOLVED">Resolved</option>
                                </select>
                                <input
                                    value={bulkNote}
                                    onChange={(event) => setBulkNote(event.target.value)}
                                    placeholder="Add quick note"
                                />
                                <button className={styles.smallActionBtn} onClick={applyBulkUpdate} disabled={!selectedCount}>
                                    Apply
                                </button>
                            </div> */}
                        </div>
                    </div>

                    <div className={styles.caseListBody}>
                        {Object.entries(groupedCases).map(([groupName, groupedItems]) => (
                            <div key={groupName} className={styles.groupSection}>
                                {groupBy !== 'NONE' && (
                                    <div className={styles.groupHeader}>
                                        <span>{groupName}</span>
                                        <span className={styles.groupCount}>{groupedItems.length}</span>
                                    </div>
                                )}

                                {groupedItems.map((item) => {
                                    // const status = getCaseStatus(item)
                                    const status = item?.currentStatus || 'OPEN'
                                    const priority = getPriorityFromSubject(item.subject)
                                    const isSelected = selectedCaseIdentifiers.includes(item.caseIdentifier)
                                    const isActive = activeCaseIdentifer === item.caseIdentifier

                                    return (
                                        <div key={item.caseIdentifier} className={isActive ? styles.eachCaseWrapperSelected : styles.eachCaseWrapper}>
                                            <div className={styles.caseRowTop}>
                                                <label className={styles.checkboxWrap}>
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => toggleCaseSelection(item.caseIdentifier)}
                                                    />
                                                </label>
                                                <span className={`${styles.statusPill} ${getStatusClass(status)}`}>{status}</span>
                                                <span className={`${styles.priorityPill} ${getPriorityClass(priority)}`}>{priority}</span>
                                            </div>

                                            <Link to={`/escalated-case/jobs/${item.caseIdentifier}`} className={styles.caseLink}>
                                                <div className={styles.caseIdentifier}>
                                                    {item.caseIdentifier}
                                                </div>

                                                <div className={styles.titleWrapper}>
                                                    <div className={styles.name}>{item.stylist_name}</div>
                                                    <div className={styles.age}>({getAge(item.stylist_birthdate)}y)</div>
                                                    <div className={styles.maritalStatus}>{item.maritalStatus}</div>
                                                    <div className={styles.gender}>{item.stylist_gender}</div>
                                                </div>

                                                <div className={styles.metaLine}><FiPhone size={11} /> {item.contact_number}</div>
                                                <div className={styles.address}><FiMapPin size={11} /> {item.currentLocation}</div>

                                                <div className={styles.domainLine}>
                                                    <span className={styles.primaryWorkDomain}>{item.primaryWorkDomain || 'No Domain'}</span>
                                                    {item.secondaryWorkDomain && <span className={styles.secondaryWorkDomain}>{item.secondaryWorkDomain}</span>}
                                                    {item.tertiaryWorkDomain && <span className={styles.tertiaryWorkDomain}>{item.tertiaryWorkDomain}</span>}
                                                </div>

                                                <div className={styles.eachRow}>
                                                    <div className={styles.eachCaseLeft}>
                                                        <div className={styles.salaryExpectation}>₹{item.minimumSalaryExpectation || 0}</div>
                                                    </div>
                                                    <div className={styles.eachCaseRight}>
                                                        <div className={styles.timeStamp}>{humanReadableTimePassed(item.addedOn)}</div>
                                                    </div>
                                                </div>
                                            </Link>
                                        </div>
                                    )
                                })}
                            </div>
                        ))}

                        {!filteredAndSortedCases.length && (
                            <div className={styles.noCaseSelected}>No cases found for current filters.</div>
                        )}
                    </div>
                </div>

                <div className={styles.rightCol}>
                    {activeCaseIdentifer === 'null' ?
                        <div className={styles.centerCol}>
                            <div className={styles.noCaseSelected}>Select a ticket to view details and thread.</div>
                        </div>
                        :
                        fetchingCaseData === true ?
                            <div className={styles.centerCol}>
                                <div className={styles.noCaseSelected}>Loading ticket data...</div>
                            </div>
                            :
                            activeCaseData === null || !activeCaseData.length ?
                                <div className={styles.centerCol}>
                                    <div className={styles.noCaseSelected}>No relevant data available.</div>
                                </div>
                                :
                                <div className={styles.centerCol}>
                                    <div className={styles.ticketCard}>
                                        <div className={styles.ticketHeader}>
                                            <div>
                                                <div className={styles.ticketSubject}>{activeCaseItem?.subject || 'Escalated Ticket'}</div>
                                                <div className={styles.ticketId}>{activeCaseItem?.caseIdentifier}</div>
                                            </div>

                                            <div className={styles.headerBadgeRow}>
                                                <span className={`${styles.statusPill} ${getStatusClass(activeCaseStatus)}`}>{activeCaseStatus.replace('_', ' ')}</span>
                                                <span className={`${styles.priorityPill} ${getPriorityClass(activeCasePriority)}`}>{activeCasePriority}</span>
                                            </div>
                                        </div>

                                        <div className={styles.ticketMetaGrid}>
                                            <div><FiUser size={12} /> {activeCaseItem?.stylist_name || '--'}</div>
                                            <div><FiPhone size={12} /> {activeCaseItem?.contact_number || '--'}</div>
                                            <div><FiMapPin size={12} /> {activeCaseItem?.currentLocation || '--'}</div>
                                            <div><FiClock size={12} /> Opened {formatDate(activeCaseItem?.addedOn)}</div>
                                        </div>

                                        <div className={styles.ticketActionsRow}>
                                            <select value={activeCaseItem?.currentStatus} onChange={(event) => updateSingleCaseStatus(event.target.value)}>
                                                <option value="">Select Option</option>
                                                {STANDARD_STATUS.map((status) => (
                                                    <option key={status} value={status}>{status?.replace('_', ' ')}</option>
                                                ))}
                                            </select>

                                            <Link target="_blank" to={`/manage-users/grow/${activeCaseData?.[0]?.stylistIdentifier}`} className={styles.ghostBtn}>
                                                View Profile <FiArrowUpRight size={11} />
                                            </Link>

                                            <a href={`/jobs-finder/${activeCaseData?.[0]?.stylistIdentifier}`} target="_blank" rel="noreferrer" className={styles.ghostBtn}>
                                                Find Jobs <FiArrowUpRight size={11} />
                                            </a>
                                        </div>

                                        <div className={styles.noteSection}>
                                            <div className={styles.noteTitle}>Internal Notes</div>
                                            {activeLocalNotes.length ?
                                                <div className={styles.notesList}>
                                                    {activeLocalNotes.slice().reverse().map((note) => (
                                                        <div key={note.id} className={styles.noteItem}>
                                                            <div className={styles.noteText}>{note.text}</div>
                                                            <div className={styles.noteMeta}>{humanReadableTimePassed(note.addedOn)} • {getExecutiveFullName(note.by)}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                                :
                                                <div className={styles.emptyNotes}>No internal notes yet.</div>
                                            }
                                        </div>
                                    </div>
                                </div>
                    }

                    <div className={styles.extremeRightCol}>
                        {fetchingCaseData === true ?
                            <div className={styles.chatWrapper}>
                                <div className={styles.chatContainer}>
                                    <div className={styles.eachOutMsgWrapper}>
                                        <div className={styles.eachOutMsgContainer}>
                                            <div className={styles.msgOutTxtLoader}>Loading conversation timeline...</div>
                                            <div className={styles.timeLoader}>please wait</div>
                                        </div>
                                    </div>

                                    <div className={styles.eachInMsgWrapper}>
                                        <div className={styles.eachInMsgContainer}>
                                            <div className={styles.msgInTxt}>Syncing case messages and actions...</div>
                                            <div className={styles.timeLoader}>loading</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            :
                            <div className={styles.chatWrapper}>
                                <div className={styles.chatHeader}>
                                    <FiMessageSquare size={14} />
                                    Conversation Thread
                                </div>
                                <div className={styles.chatContainer}>
                                    {activeCaseData?.map((item, index) => (
                                        <div className={item.msgType === 'INCOMING' ? styles.eachOutMsgWrapper : styles.eachInMsgWrapper} key={index}>
                                            <div className={item.msgType === 'INCOMING' ? styles.eachOutMsgContainer : styles.eachInMsgContainer}>
                                                <div className={item.msgType === 'INCOMING' ? styles.msgOutTxt : styles.msgInTxt}>
                                                    {item.remarks}
                                                </div>
                                                <div className={item.msgType === 'INCOMING' ? styles.timeOut : styles.timeIn}>
                                                    {humanReadableTimePassed(item.addedOn)} by {getExecutiveFullName(item.actionBy)}
                                                </div>
                                            </div>
                                        </div>
                                    ))}

                                    {!activeCaseData?.length && (
                                        <div className={styles.emptyChat}>No messages in this thread yet.</div>
                                    )}
                                </div>

                                <div className={styles.composeWrapper}>
                                    <textarea
                                        placeholder="Write an internal update or response..."
                                        onChange={(event) => setResponse(event.target.value)}
                                        value={response}
                                    />
                                    <div className={styles.sendBtn}>
                                        <button onClick={sendResponseHandler} disabled={!response.trim()}>
                                            <FiSend size={15} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        }
                    </div>
                </div>
            </div>
        </div>
    )
}

export default EscalatedCaseJobs